import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    CrossIcon,
    StyledLabel,
    StyledTextInput,
    CheckboxIcon,
    StyledSelectInput,
} from "@aureskonnect/react-ui";
import NumericInput from "react-numeric-input";
import { toast } from "react-toastify";
import { AvForm, AvGroup } from "availity-reactstrap-validation";
import classnames from "classnames";
import { useSnapshot } from "valtio";
import ErrorMessage from "../ErrorMessage";

import { clearMultiCustomSelectionData } from "@components/Common/MultiCustomSelect/store";
import { clearData } from "@components/Common/MultiCustomCreatableSelection/store";
import {
    store as device,
    setIsActionsClicked,
    setActions,
    setIsEdited,
    setIsPlusIconClicked,
} from "./store";

import {
    CustomCardBody,
    CustomCardFooter,
    CustomCardHeader,
} from "@components/Common/CustomCardStyled";
import { ConfirmationEditMessage } from "@components/Common/ConfirmationEditMessage";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";

import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";
import {
    checkOverlap,
    createDataZone,
    formatZoneLivraisonData,
    INITIAL_VALIDATION,
    INITIAL_ZONE_FORM_DATA,
} from "./hooks";
import {
    getShopVatList,
    isInvalidDistanceRange,
    removeLeadingZeros,
    resolveDefaultTva,
    resolveTvaFromList,
    scrollToFirstError,
} from "../shared";
import { PostCity } from "../City/hooks";
import ErrorToast from "@components/Common/ErrorTost";
import { setOrders, store as shopStore } from "../../store";
import { useFetchGlobal } from "../../../../../../api/hooks";
import { store as storeGlobal } from "@components/VerticalLayout/store";

type ModeOfSaleFormType = {
    isAddZone: boolean;
    setDataIsUpdated: React.Dispatch<React.SetStateAction<boolean | number>>;

    selectedRows: any;
    setLocalFilterActive: Function;
    setIsAddZone: Function;

    isAddNewDeviseButtonClicked: boolean;
    setSelectedRows: Function;
    selectedRowShops: any;
    ZoneData: any;
};

export default function ZoneForm({
    setIsAddZone,
    isAddZone,
    setDataIsUpdated,
    selectedRows,
    setLocalFilterActive,
    isAddNewDeviseButtonClicked,
    setSelectedRows,
    selectedRowShops,
    ZoneData,
}: ModeOfSaleFormType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const [title, setTitle] = React.useState<string>("");
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const { franchiseID } = useSnapshot(storeGlobal);

    const { isActionsClicked, isEdited, isPlusIconClicked } = useSnapshot(
        device
    );
    const [
        isClickedBouttonValider,
        setIsClickedBouttonValider,
    ] = React.useState<boolean>(true);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [zoneFormData, setZoneFormData] = useState(INITIAL_ZONE_FORM_DATA);
    const [validation, setValidation] = React.useState<any>(INITIAL_VALIDATION);
    const numberMatchWord = new RegExp(/^\d+([.,]\d{1,2})?$/);
    const listTva = useMemo(
        () =>
            getShopVatList(selectedRowShops).map((el) => ({
                label: el.value,
                value: el.id,
            })),
        [selectedRowShops]
    );
    const countryShop = useMemo(
        () => [
            {
                label: selectedRowShops[0]?.["Country shop"],
                value: selectedRowShops[0]?.["Country shop"],
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedRowShops[0]?.["Country shop"]]
    );
    const listDistanceZone = useMemo(
        () =>
            (ZoneData?.data ?? []).map((element: any) => ({
                end: element["FinDistance"],
                debut: element["DebutDistance"],
            })),
        [ZoneData?.data]
    );

    const resetForm = useCallback(() => {
        setIsLoading(false);
        setValidation(INITIAL_VALIDATION);
        setZoneFormData(INITIAL_ZONE_FORM_DATA);
        setDataIsUpdated?.(true);
        setIsEdited(false);
        setLocalFilterActive(false);
        clearData();
        clearMultiCustomSelectionData();
        setIsClickedBouttonValider(true);
    }, [setDataIsUpdated, setLocalFilterActive]);

    const addZone = useCallback(async () => {
        if (
            !resolveTvaFromList(
                zoneFormData.tva,
                getShopVatList(selectedRowShops)
            )
        ) {
            ErrorToast(t("Invalid VAT rate"));
            setIsLoading(false);
            return;
        }
        let localStZoneLivraison = formatZoneLivraisonData(ZoneData.data);
        setIsClickedBouttonValider(false);

        const payload = createDataZone(
            localStZoneLivraison,
            zoneFormData,
            selectedRowShops,
            selectedRows[0],
            franchiseID,
            false
        );

        try {
            const responseData: any = await PostCity(payload, fetchGlobal);
            if (responseData.error) {
                setDataIsUpdated!(true);
                throw Error("error");
            }

            const updatedDataPrice = {
                ...(shopStore.orders.dataPrice || {}),
                stZoneLivraison: payload.data,
            };

            setOrders({ dataPrice: updatedDataPrice });
            toast.success(`${t("Area added successfully")!}`, {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            });

            resetForm();
        } catch (e) {
            setIsLoading(false);
            ErrorToast(`${t("There's an error")!}`);
        }
    }, [
        ZoneData.data,
        zoneFormData,
        selectedRowShops,
        selectedRows,
        franchiseID,
        t,
        resetForm,
        setDataIsUpdated,
        fetchGlobal,
    ]);

    const editZone = React.useCallback(async () => {
        if (
            !isEdited ||
            (validation.isDistanceEdited &&
                selectedRows.length > 0 &&
                selectedRows[0][t("remote Debut")] ===
                    zoneFormData.remoteDebut &&
                selectedRows[0][t("end Distance")] === zoneFormData.endDistance)
        ) {
            setIsModalOpened(true);
            return;
        }

        if (
            !resolveTvaFromList(
                zoneFormData.tva,
                getShopVatList(selectedRowShops)
            )
        ) {
            ErrorToast(t("Invalid VAT rate"));
            setIsLoading(false);
            return;
        }

        let localStZoneLivraison = formatZoneLivraisonData(ZoneData.data);
        setIsClickedBouttonValider(false);

        const payload = createDataZone(
            localStZoneLivraison,
            zoneFormData,
            selectedRowShops,
            selectedRows[0],
            franchiseID,
            true
        );

        try {
            const responseData: any = await PostCity(payload, fetchGlobal);
            if (responseData.error) {
                setDataIsUpdated!(true);
                throw Error("error");
            }

            const updatedDataPrice = {
                ...(shopStore.orders.dataPrice || {}),
                stZoneLivraison: payload.data,
            };

            setOrders({ dataPrice: updatedDataPrice });
            toast.success(`${t("modification made successfully")!}`, {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            });

            setSelectedRows([]);
            setIsAddZone(!isAddZone);
            setIsPlusIconClicked(false);
            resetForm();
        } catch (e) {
            setIsLoading(false);
            ErrorToast(`${t("There's an error")!}`);
        }
    }, [
        ZoneData.data,
        zoneFormData,
        selectedRowShops,
        selectedRows,
        franchiseID,
        t,
        isEdited,
        validation.isDistanceEdited,
        resetForm,
        setDataIsUpdated,
        setIsModalOpened,
        setIsAddZone,
        isAddZone,
        setSelectedRows,
        fetchGlobal,
    ]);

    const updateValidation = useCallback((field: string, value) => {
        setValidation((prev: any) => ({
            ...prev,
            [field]: value,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateField = (field: any, value: any) => {
        setZoneFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleValidate = useCallback(() => {
        if (!zoneFormData.countryData) updateValidation("selectCountry", true);
        if (!zoneFormData.tva) updateValidation("inputTva", true);
        if (
            isInvalidDistanceRange(
                zoneFormData.remoteDebut,
                zoneFormData.endDistance
            )
        )
            updateValidation("isDistanceExist", true);
        if (!zoneFormData.shippingCosts.toString())
            updateValidation("isDeliveryNull", true);
        if (!zoneFormData.minimumAmount.toString())
            updateValidation("isNullMinimumAmount", false);
        if (
            zoneFormData.countryData &&
            zoneFormData.tva &&
            !isInvalidDistanceRange(
                zoneFormData.remoteDebut,
                zoneFormData.endDistance
            ) &&
            !validation.isOverlap &&
            !validation.isValidationtMinimumAmount &&
            !validation.isValidationNumber &&
            !validation.isNullMinimumAmount &&
            !validation.isDeliveryNull
        ) {
            setIsLoading(true);
            selectedRows.length === 0 ? addZone() : editZone();
        } else {
            scrollToFirstError();
        }
    }, [
        zoneFormData,
        validation,
        selectedRows,
        addZone,
        editZone,
        updateValidation,
    ]);
    const handleCancel = useCallback(() => {
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setSelectedRows([]);
                setIsAddZone(false);
                setIsEdited(false);
                clearData();
                clearMultiCustomSelectionData();
            });
        } else {
            setSelectedRows([]);
            setIsAddZone(false);
            setIsEdited(false);
            clearData();
            clearMultiCustomSelectionData();
        }
    }, [isEdited, isActionsClicked, setIsAddZone, setSelectedRows]);

    useEffect(() => {
        if (selectedRows.length > 0) {
            setTitle(t("Modify an area"));
            const row = selectedRows[0];
            setZoneFormData({
                countryData: selectedRowShops[0]["Country shop"],
                remoteDebut: row[t("remote Debut")],
                endDistance: row[t("end Distance")],
                tva: row["Tva"].toString(),
                minimumAmount: row[t("Minimum amount to place an order")],
                shippingCosts: row[t("Shipping costs")],
            });
        } else {
            setTitle(t("Add a delivery zone"));
            setZoneFormData({
                ...INITIAL_ZONE_FORM_DATA,
                tva: resolveDefaultTva(getShopVatList(selectedRowShops)),
            });
            setValidation(INITIAL_VALIDATION);
            setIsEdited(false);
            setIsClickedBouttonValider(true);
        }
        // eslint-disable-next-line
    }, [isAddNewDeviseButtonClicked, selectedRows, t, listTva]);

    React.useEffect(
        () => {
            if (isLoading) return;
            let localOverlap: boolean = checkOverlap(
                listDistanceZone,
                selectedRows,
                validation,
                zoneFormData
            );
            updateValidation("isOverlap", localOverlap);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            zoneFormData.endDistance,
            zoneFormData.remoteDebut,
            selectedRows,
            listDistanceZone,
            validation.isDistanceEdited,
            isLoading,
            t,
        ]
    );

    React.useEffect(
        () => {
            if (isPlusIconClicked || selectedRows.length > 0) {
                updateValidation("selectCountry", false);
                updateValidation("inputTva", false);
                updateValidation("isDistanceExist", false);

                setIsPlusIconClicked(false);
            }
            if (
                zoneFormData.remoteDebut !== 0 ||
                zoneFormData.endDistance !== 0
            ) {
                updateValidation("isDistanceExist", false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            isPlusIconClicked,
            selectedRows,
            zoneFormData.remoteDebut,
            zoneFormData.endDistance,
        ]
    );

    return (
        <div
            style={{
                height: "63vh",
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                paddingBottom: "20px",
            }}
        >
            <AvForm
                style={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
                <CustomCardHeader
                    className="d-flex align-items-center justify-content-between"
                    style={{ height: "76px", flexShrink: 0 }}
                >
                    <h5 className="pt-3 dev_txt_FRtitleFormCurrency">
                        {title}
                    </h5>
                    <span
                        role="button"
                        aria-label={t("Close")}
                        tabIndex={0}
                        data-testid="zone-form-close-btn"
                        className="cc-form-close-btn__clz"
                        onClick={handleCancel}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleCancel();
                            }
                        }}
                    >
                        <CrossIcon
                            className="icon-close-notification__clz dev_icn_FRcloseForm"
                            height={13}
                            width={13}
                            aria-hidden
                        />
                    </span>
                </CustomCardHeader>
                <CustomCardBody
                    className="pl-3 pr-5"
                    style={{
                        paddingBottom: "1rem",
                        flexGrow: 1,
                    }}
                >
                    <AvGroup>
                        <StyledLabel
                            htmlFor="text"
                            className="required__clz mt-3"
                        >
                            {t("Country")}
                        </StyledLabel>

                        <StyledSelectInput
                            data-testid="zone-country-select"
                            isClearable
                            autoFocus
                            noOptionsMessage={() => t("No options")}
                            options={countryShop}
                            autocomplete="off"
                            className={classnames("", {
                                input__clz: validation.selectCountry,
                            })}
                            name="change"
                            onChange={(e: any) => {
                                updateField("countryData", e?.label || "");
                                updateValidation("selectCountry", false);
                                setIsEdited(true);
                                setIsClickedBouttonValider(true);
                            }}
                            value={
                                zoneFormData.countryData
                                    ? {
                                          label: zoneFormData.countryData,
                                          value: zoneFormData.countryData,
                                      }
                                    : null
                            }
                            placeholder={`${t("Select")}…`}
                            type="text"
                        />
                        {validation.selectCountry && (
                            <ErrorMessage
                                message={t("Please select a country")}
                            />
                        )}
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel
                            htmlFor="text"
                            className="required__clz mt-3"
                        >
                            {t("Delivery area (in Km)")}
                        </StyledLabel>
                        <div className="d-flex justify-content-between ml-3">
                            <span
                                style={{
                                    top: "499px",
                                    left: "1304px",
                                    height: "21px",
                                    textAlign: "left",
                                    font:
                                        "normal normal normal 16px/21px Segoe UI",
                                    color: "#2B2828",
                                    opacity: 1,
                                }}
                                className="px-1 mt-2"
                            >
                                {t("Of")}
                            </span>
                            <NumericInput
                                className={classnames(
                                    "form-control-input-zone__clz",
                                    {
                                        input__clz:
                                            validation.isDistanceExist ||
                                            validation.isOverlap,
                                    }
                                )}
                                size={25}
                                min={0}
                                max={5000}
                                step={1}
                                onChange={(num: number | null) => {
                                    if (num !== null) {
                                        updateField("remoteDebut", num || 0);
                                        setIsEdited(true);
                                        updateValidation(
                                            "isDistanceExist",
                                            true
                                        );
                                        updateValidation(
                                            "isDistanceEdited",
                                            true
                                        );
                                    }
                                }}
                                value={zoneFormData.remoteDebut}
                                data-testid="zone-start-distance"
                            />

                            <span
                                className="ml-2 mt-2 px-1"
                                style={{
                                    top: "499px",
                                    left: "1304px",
                                    width: "25px",
                                    height: "21px",
                                    textAlign: "left",
                                    font:
                                        "normal normal normal 16px/21px Segoe UI",
                                    color: "#2B2828",
                                    opacity: 1,
                                }}
                            >
                                {t("at")}
                            </span>
                            <NumericInput
                                className={classnames(
                                    "form-control-input-zone__clz",
                                    {
                                        input__clz:
                                            validation.isDistanceExist ||
                                            validation.isOverlap ||
                                            (validation.isDistanceEdited &&
                                                isInvalidDistanceRange(
                                                    zoneFormData.remoteDebut,
                                                    zoneFormData.endDistance
                                                )),
                                    }
                                )}
                                size={25}
                                min={0}
                                max={5000}
                                step={1}
                                onChange={(num: number | null) => {
                                    updateField("endDistance", num || 0);
                                    setIsEdited(true);
                                    updateValidation("isDistanceEdited", true);
                                }}
                                value={zoneFormData.endDistance}
                                data-testid="zone-end-distance"
                            />
                        </div>
                        {(validation.isDistanceExist ||
                            (validation.isDistanceEdited &&
                                isInvalidDistanceRange(
                                    zoneFormData.remoteDebut,
                                    zoneFormData.endDistance
                                ))) && (
                            <ErrorMessage
                                message={t("Please enter a valid distance")}
                            />
                        )}

                        {validation.isOverlap && (
                            <ErrorMessage message={t("Overlap detected")} />
                        )}
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel
                            htmlFor="text"
                            className="required__clz mt-3"
                        >
                            {t("TVA")}
                        </StyledLabel>
                        <StyledSelectInput
                            data-testid="zone-tva-select"
                            isClearable
                            noOptionsMessage={() => t("No options")}
                            options={listTva}
                            autocomplete="off"
                            className={classnames("", {
                                input__clz: validation.inputTva,
                            })}
                            name="tva"
                            onChange={(e: any) => {
                                updateField("tva", e?.label || "");

                                setIsEdited(true);

                                updateValidation("inputTva", false);
                                setIsClickedBouttonValider(true);
                            }}
                            value={
                                zoneFormData.tva
                                    ? {
                                          label: zoneFormData.tva,
                                          value: zoneFormData.tva,
                                      }
                                    : null
                            }
                            placeholder={`${t("Select")}…`}
                            type="text"
                            maxMenuHeight="80px"
                        />
                        {validation.inputTva && (
                            <ErrorMessage
                                message={t("Please select a vat rate")}
                            />
                        )}
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel className="mt-3" htmlFor="Shipping">
                            {t("Shipping costs")}
                        </StyledLabel>
                        <StyledTextInput
                            data-testid="zone-shipping-input"
                            autocomplete="off"
                            id="remark"
                            name="remark"
                            value={zoneFormData.shippingCosts.toString()}
                            type="text"
                            onChange={(e: any) => {
                                setIsClickedBouttonValider(true);
                                if (e.target.value.match(numberMatchWord)) {
                                    updateValidation("isDeliveryNull", false);
                                    updateField(
                                        "shippingCosts",
                                        removeLeadingZeros(e.target.value)
                                    );
                                    updateValidation(
                                        "isValidationNumber",
                                        false
                                    );
                                } else {
                                    updateValidation(
                                        "isValidationNumber",
                                        true
                                    );
                                    updateValidation("isDeliveryNull", false);
                                }
                                if (e.target.value === "") {
                                    updateField("shippingCosts", 0);
                                    updateValidation("isDeliveryNull", true);
                                    updateValidation(
                                        "isValidationNumber",
                                        false
                                    );
                                }
                                setIsEdited!(true);
                            }}
                            className={classnames("", {
                                input__clz:
                                    validation.isValidationNumber ||
                                    validation.isDeliveryNull,
                            })}
                        />

                        {validation.isValidationNumber && (
                            <ErrorMessage
                                message={t("Please insert a number")}
                            />
                        )}

                        {validation.isDeliveryNull && (
                            <ErrorMessage
                                message={t("Please insert a delivery charge")}
                            />
                        )}
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel className="mt-3" htmlFor="remark">
                            {t("Minimum amount to place an order")}
                        </StyledLabel>
                        <StyledTextInput
                            data-testid="zone-minimum-amount-input"
                            autocomplete="off"
                            id="commande"
                            name="commande"
                            type="text"
                            onChange={(e: any) => {
                                setIsClickedBouttonValider(true);
                                const val = e.target.value;
                                if (val.match(numberMatchWord)) {
                                    updateField(
                                        "minimumAmount",
                                        removeLeadingZeros(e.target.value)
                                    );

                                    updateValidation(
                                        "isNullMinimumAmount",
                                        false
                                    );
                                    updateValidation(
                                        "isValidationtMinimumAmount",
                                        false
                                    );
                                } else {
                                    updateValidation(
                                        "isNullMinimumAmount",
                                        false
                                    );
                                    updateValidation(
                                        "isValidationtMinimumAmount",
                                        true
                                    );
                                }
                                if (val === "") {
                                    updateField("minimumAmount", 0);

                                    updateValidation(
                                        "isNullMinimumAmount",
                                        true
                                    );
                                    updateValidation(
                                        "isValidationtMinimumAmount",
                                        false
                                    );
                                }

                                setIsEdited!(true);
                            }}
                            value={zoneFormData.minimumAmount.toString()}
                            className={classnames("", {
                                input__clz:
                                    validation.isValidationtMinimumAmount ||
                                    validation.isNullMinimumAmount,
                            })}
                        />

                        {validation.isValidationtMinimumAmount && (
                            <ErrorMessage
                                message={t("Please insert a number")!}
                            />
                        )}

                        {validation.isNullMinimumAmount && (
                            <ErrorMessage
                                message={t("Please insert a Minimum Amount")!}
                            />
                        )}
                    </AvGroup>
                </CustomCardBody>
                <CustomCardFooter
                    className="p-8"
                    style={{
                        flexShrink: 0,
                        borderTop: "1px solid #dee2e6", // Optional: subtle divider
                    }}
                >
                    <CustomSecondaryColorButton
                        outline
                        rounded
                        className="mr-2 dev_btn_FRcancel"
                        variant="light"
                        onClick={handleCancel}
                    >
                        <span> {t("Cancel")}</span>
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        variant="primary"
                        className="dev_btn_FRvalidate"
                        disabled={!isClickedBouttonValider}
                        onClick={handleValidate}
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </CustomCardFooter>
            </AvForm>

            <ConfirmationEditMessage
                setDataIsUpdated={setDataIsUpdated}
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                isAddClicked={isAddZone}
                setIsAddClicked={setIsAddZone}
                setEditedData={setSelectedRows}
            />
        </div>
    );
}
